import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import axios from "axios";

interface Benefit {
  id: number;
  benefit_name: string;
  benefit_type: string;
}

interface Member {
  id: string;
  codename: string;
  gender: string;
  assigned_benefits: number[];
}

interface RecommendBenefitModalProps {
  open: boolean;
  onClose: () => void;
  onRecommend: (benefitId: number | null, memberId: string) => Promise<void>;
  benefitId?: number | null;
  memberId?: string | null;
}

const RecommendBenefitModal: React.FC<RecommendBenefitModalProps> = ({
  open,
  onClose,
  onRecommend,
  benefitId = null,
  memberId = null,
}) => {
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedBenefit, setSelectedBenefit] = useState<Benefit | null>(null);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      const fetchData = async () => {
        try {
          const [benefitsResponse, membersResponse] = await Promise.all([
            axios.get<Benefit[]>(
              `${process.env.REACT_APP_BACKEND_URL}/benefits`
            ),
            axios.get<Member[]>(
              `${process.env.REACT_APP_BACKEND_URL}/members-with-benefits`
            ),
          ]);
          setBenefits(benefitsResponse.data);
          setMembers(membersResponse.data);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [open]);

  useEffect(() => {
    if (benefitId) {
      const benefit = benefits.find((b) => b.id === benefitId) || null;
      setSelectedBenefit(benefit);
    }
    if (memberId) {
      const member = members.find((m) => m.id === memberId) || null;
      setSelectedMember(member);
    }
  }, [benefitId, memberId, benefits, members]);

  const handleRecommend = () => {
    const finalBenefitId = benefitId || selectedBenefit?.id || null;
    const finalMemberId = memberId || selectedMember?.id || "";

    if (finalBenefitId && finalMemberId) {
      onRecommend(finalBenefitId, finalMemberId);
      onClose();
    }
  };

  const isRecommendDisabled = () => {
    return (
      (!benefitId && !selectedBenefit) ||
      (!memberId && !selectedMember) ||
      (selectedBenefit &&
        selectedMember?.assigned_benefits.includes(selectedBenefit.id))
    );
  };

  const filteredBenefits = benefits.filter(
    (benefit) =>
      !selectedMember?.assigned_benefits.includes(benefit.id) &&
      !members
        .find((m) => m.id === memberId)
        ?.assigned_benefits.includes(benefit.id)
  );

  const filteredMembers = members.filter(
    (member) =>
      !member.assigned_benefits.includes(selectedBenefit?.id || benefitId || -1)
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          Recommend Benefit
        </Typography>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            {!benefitId && (
              <Autocomplete
                options={filteredBenefits}
                getOptionLabel={(option) => option.benefit_name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Benefit"
                    fullWidth
                    margin="normal"
                  />
                )}
                value={selectedBenefit}
                onChange={(_, newValue) => setSelectedBenefit(newValue)}
              />
            )}
            {!memberId && (
              <Autocomplete
                options={filteredMembers}
                getOptionLabel={(option) => `${option.codename}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Member"
                    fullWidth
                    margin="normal"
                  />
                )}
                value={selectedMember}
                onChange={(_, newValue) => setSelectedMember(newValue)}
              />
            )}
            <Button
              onClick={handleRecommend}
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              disabled={isRecommendDisabled() ? true : false}
            >
              Recommend
            </Button>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default RecommendBenefitModal;
